import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find(a => a.id === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main>
          <div className="pt-20">
            <div className="w-full px-4 md:px-6 max-w-3xl mx-auto py-20 text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
                <i className="ri-file-unknow-line text-2xl"></i>
              </div>
              <h1 className="font-heading text-xl font-semibold text-foreground-900 mb-2">
                文章不存在
              </h1>
              <p className="text-foreground-500 mb-6">该文章可能已被删除或链接无效</p>
              <Link
                to="/"
                className="whitespace-nowrap cursor-pointer inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-label hover:bg-primary-600 transition-colors"
              >
                <i className="ri-arrow-left-line"></i> 返回首页
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryName =
    article.category === 'profile' ? '个人资料' :
    article.category === 'gravure' ? '写真作品' :
    article.category === 'film' ? '影视作品' :
    article.category === 'news' ? '最新动态' : '访谈专访';

  const relatedArticles = articles
    .filter(a => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    datePublished: article.date,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '佐山爱粉丝站',
    },
    url: `https://sayaweb.github.io/article/${article.id}`,
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <Navbar />
      <main>
        <article>
          <div className="pt-20 bg-background-50">
            <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              <div className="mt-6">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="px-3 py-1 rounded-full bg-accent-50 text-accent-600 text-xs font-label">
                    {categoryName}
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-calendar-line"></i> {article.date}
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-eye-line"></i> {article.views.toLocaleString()} 次阅读
                  </span>
                  <span className="text-xs text-foreground-400 flex items-center gap-1">
                    <i className="ri-user-line"></i> {article.author}
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>

                <div className="flex flex-wrap gap-2 mb-6">
                  {article.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-1 rounded-full bg-secondary-50 text-secondary-600 text-xs font-label">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto py-0">
            <div className="relative w-full h-56 md:h-80 rounded-xl overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto pb-12">
            <div
              className="prose prose-base md:prose-lg max-w-none font-body text-foreground-800 leading-relaxed
                [&_h2]:font-heading [&_h2]:text-xl [&_h2]:md:text-2xl [&_h2]:font-semibold [&_h2]:text-foreground-900 [&_h2]:mt-10 [&_h2]:mb-4
                [&_p]:mb-5 [&_p]:leading-relaxed
                [&_strong]:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </div>

          {relatedArticles.length > 0 && (
            <div className="w-full px-4 md:px-6 max-w-3xl mx-auto pb-14">
              <div className="border-t border-background-200/70 pt-10">
                <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-5">
                  相关推荐
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="w-full h-36 rounded-lg overflow-hidden mb-3">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        />
                      </div>
                      <h4 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                        {ra.title}
                      </h4>
                      <span className="text-xs text-foreground-400 mt-1">{ra.date}</span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}